import { useEffect, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../Redux/hooks';
import { setLanguage, setTheme } from '../../../Redux/uiSlice';
import { useTranslation } from '../../../i18n/useTranslation';
import { routes } from '../../../Utils/routes';
import { ApiError } from '../../../Models/ApiModel';
import { EMPTY_PAYMENT_PROFILE, type PaymentProfileModel } from '../../../Models/UserModel';
import { PremiumFeature } from '../../../Models/enums';
import { AuthService } from '../../../Services/AuthService';
import { UserService } from '../../../Services/UserService';
import { Spinner } from '../../SharedArea/Spinner/Spinner';
import './Profile.css';

/**
 * Settings: who is signed in, how the app is displayed, and how the tutor
 * gets paid.
 *
 * The payment details are premium. The screen does not hide them; it shows
 * why they are unavailable, and the server refuses the call regardless of
 * what is drawn here.
 */
export function Profile() {
  const { tr, language } = useTranslation();
  const dispatch = useAppDispatch();
  const user = useAppSelector(state => state.session.user);
  const theme = useAppSelector(state => state.ui.theme);
  const subscription = useAppSelector(state => state.session.subscription);

  const mayEditPayment = subscription?.features.some(
    feature => feature.key === PremiumFeature.PaymentReminders && feature.accessible
  ) ?? false;

  const [profile, setProfile] = useState<PaymentProfileModel>({ ...EMPTY_PAYMENT_PROFILE });
  const [loading, setLoading] = useState(mayEditPayment);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!mayEditPayment) {
      setLoading(false);
      return;
    }
    setLoading(true);
    UserService.getPaymentProfile()
      .then(setProfile)
      .catch(() => setProfile({ ...EMPTY_PAYMENT_PROFILE }))
      .finally(() => setLoading(false));
  }, [mayEditPayment]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setSaved(false);
    setError(null);
    try {
      setProfile(await UserService.savePaymentProfile(profile));
      setSaved(true);
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.code : 'SERVER_ERROR');
    } finally {
      setBusy(false);
    }
  }

  function field(key: keyof PaymentProfileModel) {
    return {
      value: profile[key] ?? '',
      onChange: (event: { target: { value: string } }) =>
        setProfile(current => ({ ...current, [key]: event.target.value || null }))
    };
  }

  return (
    <section className="profile">
      <h1 className="screen-title">{tr('navProfile')}</h1>

      {/* On a phone the bottom bar holds five destinations, so the rest live
          here. On a desktop they are also in the sidebar; a second route to
          the same screen costs nothing and saves a hunt. */}
      <nav className="profile-links">
        <Link to={routes.receipts}>{tr('navReceipts')}</Link>
        <Link to={routes.reports}>{tr('navReports')}</Link>
        <Link to={routes.reminders}>{tr('navReminders')}</Link>
        <Link to={routes.contacts}>{tr('navContacts')}</Link>
        <Link to={routes.backup}>{tr('navBackup')}</Link>
        <Link to={routes.googleCalendar}>{tr('navGoogleCalendar')}</Link>
        <Link to={routes.paymentDocuments}>{tr('navIntakeForms')}</Link>
        <Link to={routes.premiumArea}>{tr('navPremium')}</Link>
      </nav>

      <section className="profile-panel">
        <h2>{tr('accountTitle')}</h2>
        <p className="profile-identity">
          <span>{user?.name ?? user?.email ?? ''}</span>
          {user?.email && <bdi className="profile-email">{user.email}</bdi>}
        </p>
        <button type="button" className="ghost-button" onClick={() => void AuthService.signOut()}>
          {tr('navLogout')}
        </button>
      </section>

      <section className="profile-panel">
        <h2>{tr('displayTitle')}</h2>
        <div className="profile-toggles">
          <label className="field">
            <span>{tr('languageLabel')}</span>
            <select
              className="text-input"
              value={language}
              onChange={event => dispatch(setLanguage(event.target.value === 'en' ? 'en' : 'he'))}
            >
              <option value="he">עברית</option>
              <option value="en">English</option>
            </select>
          </label>
          <label className="field">
            <span>{tr('themeLabel')}</span>
            <select
              className="text-input"
              value={theme}
              onChange={event => dispatch(setTheme(event.target.value === 'dark' ? 'dark' : 'light'))}
            >
              <option value="light">{tr('themeLight')}</option>
              <option value="dark">{tr('themeDark')}</option>
            </select>
          </label>
        </div>
      </section>

      <section className="profile-panel">
        <h2>{tr('paymentDetailsTitle')}</h2>
        {!mayEditPayment
          ? <p className="profile-locked">{tr('premiumLocked')}</p>
          : loading
            ? <Spinner />
            : (
              <form className="profile-form" onSubmit={submit}>
                <label className="field is-wide">
                  <span>{tr('payboxLink')}</span>
                  <input className="text-input" type="url" inputMode="url" {...field('payboxLink')} />
                </label>
                <label className="field is-wide">
                  <span>{tr('bitLink')}</span>
                  <input className="text-input" type="url" inputMode="url" {...field('bitLink')} />
                </label>
                <label className="field">
                  <span>{tr('bankName')}</span>
                  <input className="text-input" {...field('bankName')} />
                </label>
                <label className="field">
                  <span>{tr('bankNameEn')}</span>
                  <input className="text-input" dir="ltr" {...field('bankNameEn')} />
                </label>
                <label className="field">
                  <span>{tr('bankBranch')}</span>
                  <input className="text-input" {...field('bankBranch')} />
                </label>
                <label className="field">
                  <span>{tr('bankAccount')}</span>
                  <input className="text-input" {...field('bankAccount')} />
                </label>
                <label className="field">
                  <span>{tr('bankOwner')}</span>
                  <input className="text-input" {...field('bankOwner')} />
                </label>
                <label className="field">
                  <span>{tr('bankOwnerEn')}</span>
                  <input className="text-input" dir="ltr" {...field('bankOwnerEn')} />
                </label>
                <div className="profile-form-actions">
                  <button type="submit" className="primary-button" disabled={busy}>
                    {busy ? <Spinner inline /> : tr('save')}
                  </button>
                  {saved && <span className="profile-saved">{tr('savedNotice')}</span>}
                  {error && <span className="profile-error" role="alert">{tr('errorGeneric')}</span>}
                </div>
              </form>
            )}
      </section>
    </section>
  );
}
